import { useState, useMemo, useEffect } from 'react';
import { Stack, Group, Text, Badge, Select, Paper, Center, Loader, Button, Modal, Alert, Menu } from '@mantine/core';
import { IconEdit, IconEye, IconTrash, IconFileTypePdf, IconCheck, IconX, IconChevronDown, IconCopy, IconDownload, IconRefresh, IconFileText } from '@tabler/icons-react';
import MilkdownRenderer from '../shared/MilkdownRenderer';
import { JobArtifact, useDeleteArtifact } from '../../api/artifacts';
import { ResumeContent } from '../../api/resumes';
import { PdfStylesheet, useStylesheets } from '../../api/stylesheets';
import {
  useStartPdfGeneration,
  useGenerationStatus,
  downloadGeneratedPdf,
  type PdfGenerationJob,
} from '../../api/pdf';
import MarkdownActions from '../shared/MarkdownActions';
import { GenerationProgressIndicator } from './GenerationProgressIndicator';
import { ResumePreviewModal } from './ResumePreviewModal';
import { ResumeEditModal } from './ResumeEditModal';
import { ResumeEditModalSingle } from './ResumeEditModalSingle';
import { PdfGenerationStatusIndicator } from './PdfGenerationStatusIndicator';

interface JobResumesTabProps {
  jobResumes: JobArtifact[];
  contentItems: ResumeContent[];
  jobId: string;
  onArtifactRefresh?: () => void;
}

export default function JobResumesTab({ jobResumes, contentItems, jobId, onArtifactRefresh }: JobResumesTabProps) {
  const { data: stylesheets = [] } = useStylesheets();
  const [previewOpened, setPreviewOpened] = useState(false);
  const [editOpened, setEditOpened] = useState(false);
  const [editModalType, setEditModalType] = useState<'split' | 'crepe' | null>(null);
  const [pdfJobId, setPdfJobId] = useState<string | null>(null);
  const [showPdfSuccessAlert, setShowPdfSuccessAlert] = useState(false);

  // Mutations and queries for async PDF generation
  const startPdfGeneration = useStartPdfGeneration();
  const pdfGenerationStatus = useGenerationStatus(pdfJobId);

  // Get default stylesheet
  const defaultStylesheet = useMemo(
    () => stylesheets.find((s) => s.is_default) || null,
    [stylesheets]
  );

  // Filter by resume type and sort by creation date (newest first)
  const resumes = useMemo(() => {
    return jobResumes
      .filter((a) => a.artifact_type === 'resume')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [jobResumes]);

  // Default to most recent resume, update when resumes list changes
  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(
    resumes.length > 0 ? resumes[0].id : null
  );

  // Update selected resume when resumes list changes
  useEffect(() => {
    console.log('[JobResumesTab] Resumes list changed. Count:', resumes.length);
    if (resumes.length > 0) {
      console.log('[JobResumesTab] Available resumes:', resumes.map((r) => {
        const content = r.content_markdown || '';
        const isPlaceholder = content.includes('⏳ Generating with AI');
        return {
          id: r.id,
          name: r.name,
          contentLength: content.length,
          isPlaceholder,
          updated_at: r.updated_at,
          contentStart: content.substring(0, 50),
        };
      }));
    }

    if (resumes.length === 0) {
      console.log('[JobResumesTab] No resumes available, clearing selection');
      setSelectedResumeId(null);
      return;
    }

    // Check if there's a placeholder in the current selection
    const currentResume = resumes.find((r) => r.id === selectedResumeId);
    const isCurrentPlaceholder = currentResume?.content_markdown?.includes('⏳ Generating with AI');

    // If no selection yet, select the first (newest) which should be the placeholder
    if (!selectedResumeId) {
      console.log('[JobResumesTab] No selection yet, selecting newest resume (should be placeholder):', resumes[0].id);
      setSelectedResumeId(resumes[0].id);
      return;
    }

    // If currently selected resume is no longer in the list, select the first one
    if (!resumes.find((r) => r.id === selectedResumeId)) {
      console.log('[JobResumesTab] Currently selected resume no longer in list, switching to first:', resumes[0].id);
      setSelectedResumeId(resumes[0].id);
      return;
    }

    // If we're currently viewing a non-placeholder but there's a NEW placeholder available (at the top of the list),
    // it means generation just started, so switch to it so user can see the progress
    if (!isCurrentPlaceholder && resumes.length > 1) {
      const newestResume = resumes[0];
      const isNewestPlaceholder = newestResume.content_markdown?.includes('⏳ Generating with AI');

      if (isNewestPlaceholder && newestResume.id !== selectedResumeId) {
        console.log('[JobResumesTab] New placeholder detected, switching to show generation in progress:', newestResume.id);
        setSelectedResumeId(newestResume.id);
        return;
      }
    }
  }, [resumes, selectedResumeId]);

  const selectedResume = useMemo(
    () => resumes.find((r) => r.id === selectedResumeId),
    [resumes, selectedResumeId]
  );

  const deleteMutation = useDeleteArtifact(selectedResumeId || '');

  // Handler for Save PDF - start async generation
  const handleSavePdf = async () => {
    if (!selectedResume) return;

    try {
      console.log('[JobResumesTab] Starting PDF generation for artifact:', selectedResume.id);
      const job = await startPdfGeneration.mutateAsync({
        artifact_id: selectedResume.id,
        stylesheet_id: selectedResume.stylesheet_id || defaultStylesheet?.id,
      });

      console.log('[JobResumesTab] Got job response:', job);
      // Start polling for status
      setPdfJobId(job.job_id);
      console.log('[JobResumesTab] Set pdfJobId to:', job.job_id);
    } catch (error) {
      console.error('[JobResumesTab] Failed to start PDF generation:', error);
    }
  };

  // Handler for downloading existing cached PDF
  const handleDownloadExistingPdf = async () => {
    if (selectedResume?.pdf_storage_path) {
      try {
        await downloadGeneratedPdf(
          selectedResume.pdf_storage_path,
          `${selectedResume.name || 'resume'}.pdf`
        );
      } catch (error) {
        console.error('Failed to download existing PDF:', error);
      }
    }
  };

  // Handler for regenerating cached PDF
  const handleRegeneratePdf = async () => {
    if (selectedResume) {
      // Clear the cached PDF state first to show generation UI
      setPdfJobId(null);
      // Then trigger generation again
      await handleSavePdf();
    }
  };

  // Check if cached PDF exists
  const hasCachedPdf = selectedResume?.pdf_storage_path != null;

  // Clear PDF job ID when resume changes
  useEffect(() => {
    setPdfJobId(null);
    setShowPdfSuccessAlert(false);
  }, [selectedResumeId]);

  // When PDF generation completes, show success alert and clear job ID after a delay
  useEffect(() => {
    if (pdfGenerationStatus.data?.status === 'completed') {
      setShowPdfSuccessAlert(true);
      // Refresh artifacts to get updated pdf_storage_path
      if (onArtifactRefresh) {
        onArtifactRefresh();
      }
      // Auto-hide after 2 seconds and clear job ID to show main buttons
      const timer = setTimeout(() => {
        setPdfJobId(null);
        setShowPdfSuccessAlert(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [pdfGenerationStatus.data?.status, onArtifactRefresh]);

  // Get used content names (with fallback for old data)
  const getUsedContentNames = (resume: JobArtifact) => {
    return (resume.used_content_ids || [])
      .map((id) => contentItems.find((item) => item.id === id)?.name)
      .filter(Boolean);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this resume version? This cannot be undone.')) {
      deleteMutation.mutate();
    }
  };

  if (resumes.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Text c="dimmed">No resumes created yet</Text>
          <Text size="sm" c="dimmed">
            Use the "Create Resume" tab above to create your first resume
          </Text>
        </Stack>
      </Center>
    );
  }

  // Build select data with resume info
  const selectData = resumes.map((resume) => ({
    value: resume.id,
    label: `${resume.name} (v${resume.version}) - ${resume.status === 'published' ? '✓ Published' : 'Draft'}`,
  }));

  return (
    <Stack gap="md">
      {/* Generation progress indicator */}
      <GenerationProgressIndicator jobId={jobId} onGenerationComplete={onArtifactRefresh} />

      {/* PDF Success Alert - shown after generation completes */}
      {showPdfSuccessAlert && pdfGenerationStatus.data?.status === 'completed' && (
        <Alert
          icon={<IconCheck size={16} />}
          color="green"
          title="PDF Generated Successfully"
          mb="md"
        >
          Your PDF is ready to download. Use the "Download PDF" button below.
        </Alert>
      )}

      {/* PDF Generation Status Indicator - shown while generating or on error */}
      {pdfJobId && pdfGenerationStatus.data?.status !== 'completed' && (
        <Paper p="md" radius="md" withBorder>
          <Group justify="space-between" align="flex-start">
            <div style={{ flex: 1 }}>
              <PdfGenerationStatusIndicator
                generationJob={pdfGenerationStatus.data}
                isLoading={pdfGenerationStatus.isLoading}
              />
            </div>
            {/* Clear button to dismiss if failed or want to try again */}
            {pdfGenerationStatus.data?.status === 'failed' && (
              <Button
                variant="subtle"
                size="xs"
                onClick={() => setPdfJobId(null)}
              >
                Dismiss
              </Button>
            )}
          </Group>
        </Paper>
      )}

      {selectedResume ? (
        <Stack gap="md">
          {/* Resume heading as dropdown */}
          <Group justify="space-between" align="flex-start" gap="md">
            <div style={{ flex: 1 }}>
              <Select
                data={selectData}
                value={selectedResumeId}
                onChange={(value) => setSelectedResumeId(value || resumes[0].id)}
                placeholder="Choose a resume version"
                searchable
                styles={{
                  input: {
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    height: 'auto',
                    padding: '0.5rem',
                  },
                }}
              />
            </div>
            {/* Used content badges */}
            {getUsedContentNames(selectedResume).length > 0 && (
              <Group gap="xs" wrap="wrap">
                {getUsedContentNames(selectedResume).map((name, i) => (
                  <Badge key={i} size="sm" variant="light">
                    {name}
                  </Badge>
                ))}
              </Group>
            )}
          </Group>

          {/* Action buttons row */}
          <Group gap="xs" align="center" wrap="wrap" justify="space-between">
            <Group gap="xs" align="center">
              {/* Edit menu */}
              <Menu>
                <Menu.Target>
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconEdit size={16} />}
                    rightSection={<IconChevronDown size={14} />}
                  >
                    Edit
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconEye size={14} />}
                    onClick={() => {
                      setEditModalType('crepe');
                      setEditOpened(true);
                    }}
                  >
                    Visual Editor
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconFileText size={14} />}
                    onClick={() => {
                      setEditModalType('split');
                      setEditOpened(true);
                    }}
                  >
                    Edit Source
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              {/* Preview button */}
              <Button
                variant="light"
                size="sm"
                leftSection={<IconEye size={16} />}
                onClick={() => setPreviewOpened(true)}
              >
                Preview
              </Button>
            </Group>

            <Group gap="xs" align="center">
              {/* Export Resume menu */}
              <Menu position="bottom-start" shadow="md">
                <Menu.Target>
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconDownload size={16} />}
                    rightSection={<IconChevronDown size={14} />}
                  >
                    Export Resume
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconCopy size={14} />}
                    onClick={() => {
                      navigator.clipboard.writeText(selectedResume.content_markdown).then(() => {
                        alert('Resume markdown copied to clipboard!');
                      }).catch(() => {
                        alert('Failed to copy to clipboard');
                      });
                    }}
                  >
                    Copy Markdown to Clipboard
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconDownload size={14} />}
                    onClick={() => {
                      const element = document.createElement('a');
                      const file = new Blob([selectedResume.content_markdown], { type: 'text/markdown' });
                      element.href = URL.createObjectURL(file);
                      element.download = `${selectedResume.name}-v${selectedResume.version}.md`;
                      document.body.appendChild(element);
                      element.click();
                      document.body.removeChild(element);
                    }}
                  >
                    Download as Markdown
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              {/* Save PDF menu - Consolidate Download and Regenerate */}
              {!pdfJobId && (
                <Menu position="bottom-start" shadow="md">
                  <Menu.Target>
                    <Button
                      variant="light"
                      size="sm"
                      leftSection={<IconFileTypePdf size={16} />}
                      rightSection={<IconChevronDown size={14} />}
                    >
                      Save PDF
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {hasCachedPdf && (
                      <Menu.Item
                        leftSection={<IconDownload size={14} />}
                        onClick={handleDownloadExistingPdf}
                      >
                        Download PDF
                      </Menu.Item>
                    )}
                    <Menu.Item
                      leftSection={<IconRefresh size={14} />}
                      onClick={hasCachedPdf ? handleRegeneratePdf : handleSavePdf}
                    >
                      {hasCachedPdf ? 'Refresh PDF' : 'Generate PDF'}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          </Group>

          {/* Resume content - HTML rendered with compact variant */}
          <Paper p="xs" radius="md" withBorder>
            <MilkdownRenderer
              content={selectedResume.content_markdown}
              variant="compact"
            />
          </Paper>

          {/* Resume info row - below content */}
          <Group justify="space-between" align="flex-start" gap="md">
            {/* Left: Creation date */}
            <Text size="sm" c="dimmed">
              Created {new Date(selectedResume.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </Text>

            {/* Right: Stylesheet info */}
            {selectedResume.stylesheet_id && stylesheets.find(s => s.id === selectedResume.stylesheet_id) && (
              <Text size="sm" c="dimmed">
                Stylesheet: {stylesheets.find(s => s.id === selectedResume.stylesheet_id)?.name}
              </Text>
            )}
            {!selectedResume.stylesheet_id && defaultStylesheet && (
              <Text size="sm" c="dimmed">
                Stylesheet: {defaultStylesheet.name} (default)
              </Text>
            )}
          </Group>
        </Stack>
      ) : (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      {/* Preview Modal */}
      <ResumePreviewModal
        opened={previewOpened}
        onClose={() => setPreviewOpened(false)}
        resume={selectedResume || null}
        allStylesheets={stylesheets}
        defaultStylesheet={defaultStylesheet}
      />

      {/* Edit Modal - Split View */}
      {editModalType === 'split' && (
        <ResumeEditModal
          opened={editOpened}
          onClose={() => {
            setEditOpened(false);
            setEditModalType(null);
          }}
          resume={selectedResume || null}
          allStylesheets={stylesheets}
          defaultStylesheet={defaultStylesheet}
          jobId={jobId}
          onSuccess={onArtifactRefresh}
        />
      )}

      {/* Edit Modal - Crepe Editor */}
      {editModalType === 'crepe' && (
        <ResumeEditModalSingle
          opened={editOpened}
          onClose={() => {
            setEditOpened(false);
            setEditModalType(null);
          }}
          resume={selectedResume || null}
          jobId={jobId}
          onSuccess={onArtifactRefresh}
        />
      )}
    </Stack>
  );
}
